import { useEffect, useState } from 'react'
import isEmpty from 'lodash-es/isEmpty'

import { EMPTY_OBJECT } from '../../../../../constants/common'
import { useRequestQueue } from '../../../../../hooks'
import { getUserProfileTabCounters } from '../../UserProfileTabs.api'
import {
  getHasUserProfileTabCounters,
  getUserProfileTabCountersParams,
} from '../../UserProfileTabs.utils'

export function useUserProfileTabsCounters({ tabs, userData }) {
  // `entityId` of the counters that landed: the readiness of the current user.
  const [loaded, setLoaded] = useState(EMPTY_OBJECT)

  const { enqueue } = useRequestQueue()

  const userId = userData?.id

  async function handleLoadCounters(tabIds = null) {
    const params = getUserProfileTabCountersParams({ tabIds, tabs, userData })

    // No flags makes the backend return every section.
    if (isEmpty(params)) {
      return null
    }

    // Queued: a mount-time full response must not land after an action-driven one.
    return enqueue(() => getUserProfileTabCounters({ params, userId }))
      .then(response => {
        const data = response?.data || EMPTY_OBJECT

        setLoaded(previousLoaded => ({
          counters: tabIds ? { ...previousLoaded.counters, ...data } : data,
          entityId: userId,
        }))

        return response
      })
      .catch(() => {
        if (!tabIds) {
          setLoaded({ counters: EMPTY_OBJECT, entityId: userId })
        }

        return null
      })
  }

  useEffect(() => {
    if (userId) {
      handleLoadCounters()
    }
  }, [userId])

  // No viewable counter sections — nothing will ever land, so the counters are ready as is.
  const isCountersReady =
    userId != null &&
    (loaded.entityId === userId ||
      !getHasUserProfileTabCounters({ tabs, userData }))

  return {
    counters: (isCountersReady && loaded.counters) || EMPTY_OBJECT,
    handleLoadCounters,
    isCountersReady,
  }
}
